import { useOpenThread } from '@zuruflex/api-client/react';
import {
  MAX_SUPPORT_SUBJECT_CHARS,
  SUPPORT_TOPICS_FOR,
  defaultSupportTopic,
  shouldAskCsat,
  validateSupportRequest,
  type SupportRequestProblem,
} from '@zuruflex/core';
import { MAX_MESSAGE_CHARS, type MessageThreadDetail, type ThreadTopic } from '@zuruflex/types';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@zuruflex/ui';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from './api.js';

/**
 * Asking Zuruflex for help, from wherever an owner is standing.
 *
 * The dialog used to live inside the inbox page, which meant the only door
 * was the inbox: somebody looking at the booking they had a question about
 * had to leave it, and retype a reference they could no longer see. It is
 * a provider now, mounted around the router's outlet, so a page that
 * already knows which hire or which car this is about opens it with that
 * filled in.
 *
 * The rules are not here. Which topics may be filed under, what a
 * contextual door pre-selects and whether what has been typed will survive
 * the API all live in `@zuruflex/core`'s support module, which the website,
 * the admin desk and both phone apps read as well — see the header of
 * `packages/core/src/support-rules.ts`. This file is the portal's *idiom*
 * for the same flow, and it is deliberately the website's: a dialog rather
 * than a pushed screen, a `Select` rather than a run of radio rows, the
 * same fields in the same order, the same four states.
 */

export interface SupportRequestContext {
  /**
   * Pre-selects the topic. Omitted, it follows `defaultSupportTopic` for
   * the `PARTNER` realm: a booking beats a car, and a door that knows
   * neither means `PARTNER`.
   */
  topic?: ThreadTopic;
  bookingId?: string;
  /**
   * Only to draw the context line (`common:support.aboutBooking`). The id
   * is what the request carries; the reference is what an owner
   * recognises, and only the door knows it.
   */
  bookingReference?: string;
  vehicleId?: string;
  /** The same, for a car — `common:support.aboutVehicle`. */
  vehicleName?: string;
  /** A starting subject, editable like every other field. */
  subject?: string;
}

interface SupportState {
  openSupport: (context?: SupportRequestContext) => void;
  isOpen: boolean;
}

const SupportContext = createContext<SupportState | null>(null);

/**
 * Holds the one Ask Zuruflex dialog the whole portal shares.
 *
 * No sign-in gate, unlike the website's: everything under `AppLayout` is
 * already behind `RequireAuth`, so there is nobody here without an account
 * for the thread to belong to.
 */
export function SupportProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<SupportRequestContext | null>(null);

  const openSupport = useCallback((context: SupportRequestContext = {}) => {
    setRequest(context);
  }, []);

  const value = useMemo<SupportState>(
    () => ({ openSupport, isOpen: request !== null }),
    [openSupport, request],
  );

  return (
    <SupportContext.Provider value={value}>
      {children}
      <AskZuruflexDialog
        open={request !== null}
        onOpenChange={(next) => {
          if (!next) setRequest(null);
        }}
        request={request}
      />
    </SupportContext.Provider>
  );
}

export function useSupport(): SupportState {
  const context = useContext(SupportContext);
  if (context === null) throw new Error('useSupport must be used inside <SupportProvider>');
  return context;
}

/**
 * What the topic control starts on.
 *
 * The door's own topic wins; otherwise it is `defaultSupportTopic`'s call,
 * in `@zuruflex/core`, for the `PARTNER` realm. That function is where the
 * "owner with no context is asking about being an owner" rule lives now —
 * it used to be spelled here and nowhere else, so the owner's *phone*
 * opened the same bare Ask Zuruflex on `OTHER` and one owner's one question
 * reached two different desks depending on which screen they were holding.
 *
 * A pre-selection either way. The control stays editable, because the
 * person writing knows what this is about and we are guessing.
 */
function defaultTopic(context: SupportRequestContext): ThreadTopic {
  if (context.topic) return context.topic;
  return defaultSupportTopic('PARTNER', context);
}

/**
 * The support form, in the portal.
 *
 * Same fields in the same order as the other four surfaces — what this is
 * about, the topic, an optional subject, the message, one Send — and the
 * same words, all of them, out of `common:support.*`. The portal used to
 * respell the title, the topic label, the subject label and the submit as
 * `partner:inbox.askZuruflex*`; the topic label happened to be word for word
 * identical in all three locales and the other three were not, so an owner
 * filing from the portal read "Ask Zuruflex" / "Subject (optional)" / "Send to
 * Zuruflex" where the same owner on their phone read "Get help" / "Subject" /
 * "Send". Those keys are retired. "Ask Zuruflex" survives as the *door* —
 * `common:support.entryLabel`, which all five surfaces now read — because
 * that is what the button an owner pressed said.
 *
 * `validateSupportRequest` decides whether Send is live, so the client
 * allows exactly what the API accepts. The bounds are on the controls
 * themselves, which is why there is no "too long" to render: nothing
 * longer can be typed in the first place.
 *
 * It ends on the confirmation rather than on the conversation. This can be
 * opened over a booking row or a car being set up, and navigating to the
 * thread would cost somebody the page they asked from — which is the one
 * thing a dialog exists to avoid. The way through is a link they choose.
 */
function AskZuruflexDialog({
  open,
  onOpenChange,
  request,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What the door already knew. Null while the dialog animates shut. */
  request: SupportRequestContext | null;
}) {
  const { t } = useTranslation(['partner', 'common']);

  /*
   * The last request, kept through the closing animation: `request` is
   * null the moment the dialog starts to shut, and reading it directly
   * would empty every field in front of somebody on the way out.
   */
  const held = useRef<SupportRequestContext>({});
  if (request) held.current = request;
  const context = held.current;

  /*
   * Every heading, `PARTNER` included. It is the renter side that loses
   * that one — an owner is exactly the person who files under it — and
   * filtering from the one constant is what keeps a topic added tomorrow
   * appearing on all five surfaces at once, in the same order.
   */
  const topics = SUPPORT_TOPICS_FOR('PARTNER');

  const [topic, setTopic] = useState<ThreadTopic>('PARTNER');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  /** The thread that was created, which is the success state. */
  const [sentId, setSentId] = useState<string | null>(null);

  /*
   * Which fields have been left, so an objection is only put to somebody
   * who has actually been there. An empty form that has never been touched
   * says nothing; a message box that was typed in and emptied again says
   * what is missing, in `common:support.problem.*` — the same sentences on
   * all four forms, out of the one validator.
   */
  const [touched, setTouched] = useState<Partial<Record<SupportRequestProblem['field'], boolean>>>(
    {},
  );

  const openThread = useOpenThread(api);

  /*
   * One key per opening, not per attempt: a Send that fails and is pressed
   * again is the same request, and a fresh key on the retry would file the
   * ticket twice if the first one did land.
   */
  const idempotencyKey = useRef<string>('');

  useEffect(() => {
    if (!open) return;
    setTopic(defaultTopic(request ?? {}));
    setSubject(request?.subject ?? '');
    setBody('');
    setTouched({});
    setSentId(null);
    openThread.reset();
    idempotencyKey.current = crypto.randomUUID();
    // `openThread` is a fresh object each render; this resets on opening,
    // which is what `open` and the request it opened with say.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request]);

  const validation = validateSupportRequest({ topic, subject, body });

  const problem = (field: SupportRequestProblem['field']): string | undefined => {
    if (validation.valid || !touched[field]) return undefined;
    const found = validation.problems.find((one) => one.field === field);
    return found
      ? t(`common:support.problem.${found.field}.${found.code}`, { max: found.max })
      : undefined;
  };

  const submit = () => {
    if (!validation.valid || openThread.isPending) return;
    // The *normalised* fields rather than what is in state, so what the
    // client measured is what the server is handed.
    openThread.mutate(
      {
        kind: 'SUPPORT',
        topic: validation.value.topic,
        body: validation.value.body,
        ...(validation.value.subject ? { subject: validation.value.subject } : {}),
        ...(context.bookingId ? { bookingId: context.bookingId } : {}),
        ...(context.vehicleId ? { vehicleId: context.vehicleId } : {}),
        idempotencyKey: idempotencyKey.current,
      },
      { onSuccess: (thread) => setSentId(thread.id) },
    );
  };

  const about = context.bookingReference
    ? t('common:support.aboutBooking', { reference: context.bookingReference })
    : context.vehicleName
      ? t('common:support.aboutVehicle', { vehicle: context.vehicleName })
      : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('common:support.title')}</DialogTitle>
          <DialogDescription>{t('common:support.lead')}</DialogDescription>
        </DialogHeader>

        {sentId ? (
          <>
            <Alert variant="success">{t('common:support.sent')}</Alert>
            <DialogFooter>
              <Button asChild>
                <Link to={`/inbox/${sentId}`} onClick={() => onOpenChange(false)}>
                  {t('nav.inbox')}
                </Link>
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              submit();
            }}
          >
            {/* What this is about, before anything is asked — the same line
                in the same place as the phone's compose screen. An owner
                who opened this from the wrong row finds out here, rather
                than after typing a paragraph. */}
            {about ? (
              <p className="rounded-card bg-surface-sunken px-3 py-2 text-sm text-text-muted">
                {about}
              </p>
            ) : null}

            <FormField label={t('common:support.topicLabel')} required error={problem('topic')}>
              {(field) => (
                <Select value={topic} onValueChange={(value) => setTopic(value as ThreadTopic)}>
                  <SelectTrigger id={field.id}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {topics.map((value) => (
                      <SelectItem key={value} value={value}>
                        {t(`common:support.topic.${value}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>

            {/*
              Optional, and worth having: the inbox row shows a subject
              where it can, and "Payout" scans faster down a queue than the
              first line of a paragraph about a payout.
            */}
            <FormField label={t('common:support.subject')} error={problem('subject')}>
              {(field) => (
                <Input
                  {...field}
                  value={subject}
                  maxLength={MAX_SUPPORT_SUBJECT_CHARS}
                  placeholder={t('common:support.subjectPlaceholder')}
                  onChange={(event) => setSubject(event.target.value)}
                  onBlur={() => setTouched((was) => ({ ...was, subject: true }))}
                />
              )}
            </FormField>

            {/*
              The label and the prompt inside the box are different
              sentences here as they are on the website, so the field does
              not say "What has happened?" twice.
            */}
            <FormField label={t('common:support.body')} required error={problem('body')}>
              {(field) => (
                <Textarea
                  {...field}
                  value={body}
                  rows={5}
                  maxLength={MAX_MESSAGE_CHARS}
                  placeholder={t('common:support.bodyPlaceholder')}
                  onChange={(event) => setBody(event.target.value)}
                  onBlur={() => setTouched((was) => ({ ...was, body: true }))}
                />
              )}
            </FormField>

            {/* A control that silently does nothing is the worst answer
                there is: a refused request says so, every time. */}
            {openThread.isError ? (
              <Alert variant="danger">{t('common:support.error')}</Alert>
            ) : null}

            <DialogFooter>
              <Button
                type="submit"
                disabled={!validation.valid || openThread.isPending}
                isLoading={openThread.isPending}
              >
                {openThread.isPending ? t('common:support.sending') : t('common:support.send')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ── How did we do? ─────────────────────────────────────────── */

/**
 * The last prompt this browser drew, and on which thread.
 *
 * `shouldAskCsat` caps the ask at one a week *per person*, and where that
 * is remembered differs by surface — the desk has it on the row, a phone
 * and a browser keep it themselves. The thread id travels with the
 * timestamp because the cap is about *other* threads: without it, drawing
 * the prompt would record an ask that suppressed the prompt on screen.
 *
 * The same key names as the website's, which costs nothing and means the
 * two read identically — they are separate origins, so neither can see the
 * other's storage whatever they are called.
 */
const ASKED_KEY = 'zuruflex.csat.asked';

/** Threads this browser was told to leave alone. Local by design — see `CsatAskOptions`. */
const DISMISSED_KEY = 'zuruflex.csat.dismissed';

interface AskRecord {
  threadId: string;
  at: string;
}

// Every storage access is guarded: a private window, blocked site data or
// a full quota all throw, and none of them should cost somebody their
// conversation. Without a memory the rule still holds — the API's own
// stamp is what decides whether there is anything to ask about.
function readAsk(): AskRecord | null {
  try {
    const raw = window.localStorage.getItem(ASKED_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<AskRecord> | null;
    if (typeof value?.threadId !== 'string' || typeof value.at !== 'string') return null;
    return { threadId: value.threadId, at: value.at };
  } catch {
    return null;
  }
}

function writeAsk(record: AskRecord): void {
  try {
    window.localStorage.setItem(ASKED_KEY, JSON.stringify(record));
  } catch {
    // The prompt still works for this page view.
  }
}

function readDismissed(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    if (!raw) return {};
    const value = JSON.parse(raw) as unknown;
    return typeof value === 'object' && value !== null ? (value as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeDismissed(threadId: string, at: string): void {
  try {
    window.localStorage.setItem(
      DISMISSED_KEY,
      JSON.stringify({ ...readDismissed(), [threadId]: at }),
    );
  } catch {
    // Waved away for this page view, asked again on the next one.
  }
}

export interface CsatPromptState {
  /** Whether to draw the prompt at all. */
  visible: boolean;
  /** "Not now" — this thread, this browser, for good. */
  dismiss: () => void;
}

/**
 * Whether this conversation should be asking how the help went.
 *
 * The decision is `shouldAskCsat`'s, not this hook's: support kind,
 * resolved, asked but unanswered, inside the cooldown and not a fortnight
 * stale. All this adds is the two facts that rule needs and no server
 * holds — when this browser last drew a prompt, and whether this thread's
 * was waved away.
 *
 * It stays visible once drawn, until the thread changes. The score is
 * answered in place and `CsatPrompt` draws the thank-you off the row that
 * comes back, which it cannot do if the moment of answering also takes the
 * panel off the screen.
 */
export function useCsatPrompt(thread: MessageThreadDetail | undefined): CsatPromptState {
  const id = thread?.id;
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);
  const [drawn, setDrawn] = useState(false);

  // A different conversation is a different question, so both local facts
  // are read again rather than carried across.
  useEffect(() => {
    setDrawn(false);
    setDismissedAt(id ? (readDismissed()[id] ?? null) : null);
  }, [id]);

  /*
   * Read once per thread rather than per render: the effect below writes
   * this key, and re-reading it afterwards would hand `shouldAskCsat` the
   * ask it has just recorded and close the prompt it opened.
   */
  const lastAskedAt = useMemo(() => {
    if (!id) return null;
    const record = readAsk();
    return record && record.threadId !== id ? record.at : null;
  }, [id]);

  const ask = thread ? shouldAskCsat(thread, { now: new Date(), lastAskedAt, dismissedAt }) : false;

  useEffect(() => {
    if (!ask || !id) return;
    setDrawn(true);
    // Keeps the original timestamp when this thread is reopened: the ask
    // went out once, and re-dating it would push the next thread's prompt
    // a week further out every time somebody scrolled back.
    if (readAsk()?.threadId !== id) writeAsk({ threadId: id, at: new Date().toISOString() });
  }, [ask, id]);

  const dismiss = useCallback(() => {
    if (!id) return;
    const at = new Date().toISOString();
    setDismissedAt(at);
    writeDismissed(id, at);
  }, [id]);

  return { visible: drawn && dismissedAt === null, dismiss };
}
